/**
 * The page math for the DataTable's rows-to-display selector.
 *
 * Sorting and filtering always run over the whole dataset; this only decides
 * which slice of the sorted rows is rendered at once. Kept pure and away from
 * React so it can be unit-tested on its own (the web tests run in Node).
 */

/** How many rows to render at once: a fixed size, or every row. */
export type RowsSetting = 50 | 100 | 'all';

export const ROWS_OPTIONS: RowsSetting[] = [50, 100, 'all'];

export const DEFAULT_ROWS: RowsSetting = 100;

export interface PageView {
  /** The page actually shown, clamped into range. */
  page: number;
  /** How many pages the rows setting divides the data into (1 for "all"). */
  pageCount: number;
  /** Half-open slice of the sorted rows to render: `rows.slice(start, end)`. */
  start: number;
  end: number;
  /** 1-based row numbers for the "rows a–b of n" label; both 0 when empty. */
  from: number;
  to: number;
  total: number;
  /** Whether the compact pager should show (a fixed size with rows to page). */
  showPager: boolean;
}

/**
 * Resolve a requested page against the data size and rows setting. `page` is
 * clamped, so a page left stranded past the end after a filter falls back to
 * the last page rather than showing nothing.
 */
export function pageView(total: number, rows: RowsSetting, requestedPage: number): PageView {
  const size = rows === 'all' ? Math.max(total, 1) : rows;
  const pageCount = Math.max(1, Math.ceil(total / size));
  const page = Math.min(Math.max(1, Math.trunc(requestedPage) || 1), pageCount);

  const start = (page - 1) * size;
  const end = Math.min(total, start + size);

  return {
    page,
    pageCount,
    start,
    end,
    from: total === 0 ? 0 : start + 1,
    to: end,
    total,
    showPager: rows !== 'all' && total > 0,
  };
}

/** The label under the pager: "rows 1–50 of 721", or "No rows" when empty. */
export function rangeLabel(view: PageView): string {
  if (view.total === 0) return 'No rows';
  return `rows ${view.from}–${view.to} of ${view.total}`;
}

/** A rows setting parsed from stored or user input; the default when unknown. */
export function parseRowsSetting(value: string | null | undefined): RowsSetting {
  if (value === 'all') return 'all';
  const n = Number(value);
  if (n === 50 || n === 100) return n;
  return DEFAULT_ROWS;
}
